import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faFileLines, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
    createParkingGatewayFormSchema,
    type CreateParkingGatewayFormSchemaValues,
    useCreateParkingGateway,
} from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useCreateParkingGateway';
import {
    type ParkingGateways,
    RESOURCE as QUERY_CACHE_KEY,
} from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useParkingGateways';
import ParkingGatewayForm from 'src/features/parkingManagement/components/EntryExitGateways/ParkingGatewayForm';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
    DialogContent,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { DataTableNewEntryProps } from 'src/registry/components/table';

interface AddParkingGatewayProps extends DataTableNewEntryProps<ParkingGateways> {}

const AddParkingGateway = ({ onSubmit: onSubmitCallback }: AddParkingGatewayProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const { toast } = useToast();

    const form = useForm<CreateParkingGatewayFormSchemaValues>({
        resolver: zodResolver(createParkingGatewayFormSchema),
        mode: 'onChange',
        defaultValues: {
            parking: parkingId ?? '',
            gateway_type: undefined,
            name: undefined,
            address: undefined,
            longitude: undefined,
            latitude: undefined,
            max_height_cm: null,
            max_width_cm: null,
        },
    });

    const { mutate, isPending } = useCreateParkingGateway({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [QUERY_CACHE_KEY],
            });

            onOpenChange(false);
            toast({
                title: formatMessage(parkingManagementMessages.addNewPointSuccess),
                description: formatMessage(parkingManagementMessages.addNewPointSuccessDescription),
            });
            if (onSubmitCallback) {
                onSubmitCallback();
            }
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = (values: CreateParkingGatewayFormSchemaValues) => {
        const longitude = Number(values.longitude);
        const latitude = Number(values.latitude);

        if (isNaN(longitude) || isNaN(latitude)) {
            throw new Error(formatMessage(parkingManagementMessages.invalidCoordinates));
        }

        const submitData = {
            ...values,
            coordinates: {
                type: 'Point',
                coordinates: [longitude, latitude],
            },
        };
        mutate(submitData);
        form.reset();
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    const {
        formState: { isValid, isDirty },
    } = form;

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button variant="default" className={cn('mb-4 ml-auto')}>
                    <FontAwesomeIcon icon={faPlus} />
                    {formatMessage(parkingManagementMessages.addNewPoint)}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faFileLines} />
                        <div>
                            <DialogTitle>{formatMessage(parkingManagementMessages.newEntryExit)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(parkingManagementMessages.addNewPointDescription)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ParkingGatewayForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1" onClick={() => form.reset()}>
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={!isValid || !isDirty}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(parkingManagementMessages.addNewPoint)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AddParkingGateway;
